import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { slugify } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import MobileGridSubcategoryTree from './MobileGridSubcategoryTree';

const DynamicFormattedMessage = FormattedMessage;

const CategoryTreeContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gridCategoryTreeTextColor};
  background-color: white;
  width: 100%;
  padding: 8px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08), inset 0 1px 0 0 #e6e6e6;
`;

const CategoryTreeList = styled.ul`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  list-style: none;
  letter-spacing: -0.4px;
`;

const CategoryTreeLink = styled.a`
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

const CategoryTreeItemLinkText = styled.p`
  padding: 19px 8px;
  border-radius: 4px;
  background-color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.colors.gridCategoryTreeActiveBackgroundColor
      : 'transparent'};
`;

const MobileGridCategoryTree = ({ activeCategory }) => {
  const ancestor = activeCategory?.isTopLevel
    ? activeCategory
    : activeCategory?.ancestors?.[0];

  // The top level tree has lots of siblings by nature of the current categoryNavData from useNav
  // We need to extra only a subset for the top level tree
  const topLevelTree = activeCategory?.siblings?.find(
    (category) => category?.urlPath === activeCategory?.urlPath
  )?.subcategories;
  const nonTopLevelTree = activeCategory?.siblings;
  const tree = activeCategory?.isTopLevel ? topLevelTree : nonTopLevelTree;

  if (!activeCategory) {
    return null;
  }

  return (
    <CategoryTreeContainer>
      <CategoryTreeList>
        {ancestor && (
          <li data-autotag="grid-category-dd-option">
            <CategoryTreeLink href={ancestor?.urlPath}>
              <CategoryTreeItemLinkText
                $isActive={ancestor?.urlPath === activeCategory?.urlPath}
              >
                <DynamicFormattedMessage
                  id={`site_product_category.${slugify(ancestor.label)}`}
                  defaultMessage={ancestor.label}
                />
              </CategoryTreeItemLinkText>
            </CategoryTreeLink>
          </li>
        )}
        {tree.map((category) => {
          return (
            <li key={category?.urlPath} data-autotag="grid-category-dd-option">
              <CategoryTreeLink href={category?.urlPath}>
                <CategoryTreeItemLinkText
                  $isActive={category?.urlPath === activeCategory?.urlPath}
                  style={{ paddingLeft: '32px' }}
                >
                  <DynamicFormattedMessage
                    id={`site_product_category.${slugify(category.label)}`}
                    defaultMessage={category.label}
                  />
                </CategoryTreeItemLinkText>
              </CategoryTreeLink>
              <MobileGridSubcategoryTree
                subcategories={category?.subcategories}
              />
            </li>
          );
        })}
      </CategoryTreeList>
    </CategoryTreeContainer>
  );
};

MobileGridCategoryTree.propTypes = {
  activeCategory: PropTypes.shape({
    urlPath: PropTypes.string,
    label: PropTypes.string,
    isTopLevel: PropTypes.bool,
    ancestors: PropTypes.arrayOf(
      PropTypes.shape({
        urlPath: PropTypes.string,
        label: PropTypes.string,
      })
    ),
    siblings: PropTypes.arrayOf(
      PropTypes.shape({
        urlPath: PropTypes.string,
        label: PropTypes.string,
        subcategories: PropTypes.arrayOf(
          PropTypes.shape({
            urlPath: PropTypes.string,
            label: PropTypes.string,
          })
        ),
      })
    ),
  }),
};

export default MobileGridCategoryTree;
